import { gsap } from 'gsap'
import { CustomEase } from 'gsap/CustomEase'

gsap.registerPlugin(CustomEase)
CustomEase.create('osmo', 'M0,0 C0.625,0.05 0,1 1,1')

const HERO_SELECTOR = '[data-hero]'
const SLOT_SELECTOR = '[data-animated-word-slot]'
const TRACK_SELECTOR = '[data-animated-word-track]'
const WORD_SELECTOR = '[data-animated-word]'
const PRODUCT_SELECTOR = '[data-hero-product]'
const WORD_STAGGER = 0.08
const WORD_INITIAL_DELAY = 0.5
const WORD_CHANGE_DELAY = 1.6
const WORD_REVEAL_GAP = 4
const PRODUCT_Y_OFFSET = 220
const PRODUCT_DURATION = 1.8
const PRODUCT_IN_DELAY = 0
const PRODUCT_START_OPACITY = 0.3

const initHeroAnimation = (root = document) => {
    const heroes = root.querySelectorAll(HERO_SELECTOR)

    if (!heroes.length) {
        return
    }

    heroes.forEach((hero) => {
        if (hero.hasAttribute('data-hero-initialized')) {
            return
        }

        hero.setAttribute('data-hero-initialized', '')

        const slots = Array.from(hero.querySelectorAll(SLOT_SELECTOR))
            .map((slot) => {
                const track = slot.querySelector(TRACK_SELECTOR)
                const words = track
                    ? Array.from(track.querySelectorAll(WORD_SELECTOR))
                    : []

                return {
                    slot,
                    track,
                    words
                }
            })
            .filter(({ track, words }) => track && words.length > 1)

        if (!slots.length) {
            return
        }

        const firstSlotWordCount = slots[0].words.length
        const rowCount = Math.min(
            ...slots.map(({ words }) => words.length),
            firstSlotWordCount
        )

        if (rowCount < 2) {
            return
        }

        const products = Array.from(
            hero.querySelectorAll(PRODUCT_SELECTOR)
        ).slice(0, rowCount)
        let productLoopItems = products

        const prefersReduced = window.matchMedia(
            '(prefers-reduced-motion: reduce)'
        ).matches

        if (prefersReduced) {
            slots.forEach(({ track }) => gsap.set(track, { y: 0 }))
            products.forEach((product, productIndex) => {
                gsap.set(product, {
                    xPercent: -50,
                    yPercent: -50,
                    y: 0,
                    autoAlpha: productIndex === 0 ? 1 : 0
                })
            })
            return
        }

        slots.forEach(({ slot, track, words }) => {
            const firstWordClone = words[0].cloneNode(true)
            firstWordClone.setAttribute('aria-hidden', 'true')
            track.appendChild(firstWordClone)

            const allWords = Array.from(track.querySelectorAll(WORD_SELECTOR))
            const rowHeight = Math.max(
                ...allWords.map((word) => word.offsetHeight)
            )
            const rowGap = rowHeight * WORD_REVEAL_GAP

            gsap.set(allWords.slice(1), { marginTop: rowGap })

            gsap.set(slot, { height: rowHeight })
            slot.style.setProperty(
                '--hero-word-step',
                `${rowHeight + rowGap}px`
            )
            gsap.set(track, { y: 0 })
        })

        if (products.length > 1) {
            const firstProductClone = products[0].cloneNode(true)
            firstProductClone.setAttribute('aria-hidden', 'true')
            products[0].parentNode.appendChild(firstProductClone)
            productLoopItems = [...products, firstProductClone]
        }

        productLoopItems.forEach((product, productIndex) => {
            gsap.set(product, {
                xPercent: -50,
                yPercent: -50,
                y: productIndex === 0 ? 0 : PRODUCT_Y_OFFSET,
                autoAlpha: productIndex === 0 ? 1 : 0
            })
        })

        const tl = gsap.timeline({
            repeat: -1,
            repeatDelay: 0.8
        })

        for (let rowIndex = 1; rowIndex <= rowCount; rowIndex++) {
            const timelinePosition =
                rowIndex === 1 ? WORD_INITIAL_DELAY : `+=${WORD_CHANGE_DELAY}`
            const currentProduct = productLoopItems[rowIndex - 1]
            const nextProduct = productLoopItems[rowIndex]

            tl.to(
                slots.map(({ track }) => track),
                {
                    y: (_, track) => {
                        const slot = track.closest(SLOT_SELECTOR)
                        const step = parseFloat(
                            slot.style.getPropertyValue('--hero-word-step')
                        )

                        return -step * rowIndex
                    },
                    duration: 1.4,
                    ease: 'osmo',
                    stagger: WORD_STAGGER
                },
                timelinePosition
            )

            if (currentProduct && nextProduct) {
                tl.to(
                    currentProduct,
                    {
                        y: -PRODUCT_Y_OFFSET,
                        autoAlpha: 0,
                        duration: PRODUCT_DURATION,
                        ease: 'osmo'
                    },
                    '<'
                )

                tl.fromTo(
                    nextProduct,
                    {
                        y: PRODUCT_Y_OFFSET,
                        autoAlpha: 0
                    },
                    {
                        y: 0,
                        autoAlpha: PRODUCT_START_OPACITY,
                        duration: PRODUCT_DURATION,
                        ease: 'osmo'
                    },
                    `<+=${PRODUCT_IN_DELAY}`
                )

                tl.to(
                    nextProduct,
                    {
                        autoAlpha: 1,
                        duration: WORD_CHANGE_DELAY,
                        ease: 'osmo'
                    },
                    '>'
                )
            }
        }

        tl.set(
            slots.map(({ track }) => track),
            { y: 0 }
        )

        if (products.length) {
            tl.set(productLoopItems.slice(1), {
                y: PRODUCT_Y_OFFSET,
                autoAlpha: 0
            })
            tl.set(products[0], {
                y: 0,
                autoAlpha: 1
            })
        }
    })
}

export const initWhenReady = (root = document) => {
    if (document.fonts) {
        document.fonts.ready.then(() => initHeroAnimation(root))
        return
    }

    initHeroAnimation(root)
}

document.addEventListener('DOMContentLoaded', () => {
    initWhenReady()

    if (window.htmx !== undefined) {
        htmx.on('htmx:afterRequest', function () {
            initHeroAnimation()
        })
    }
})
